import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Upload, FileText, Download, Trash2, ExternalLink, Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { format } from "date-fns";

interface ProposalDocumentsProps {
  proposalId: string;
  isReadonly?: boolean;
}

export function ProposalDocuments({ proposalId, isReadonly = false }: ProposalDocumentsProps) {
  const [uploading, setUploading] = useState(false);
  const [driveUrl, setDriveUrl] = useState("");
  const [isAddingDriveLink, setIsAddingDriveLink] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch documents for this proposal
  const { data: documents, isLoading } = useQuery({
    queryKey: ["proposal-documents", proposalId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("proposal_documents")
        .select("*")
        .eq("proposal_id", proposalId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!proposalId,
  });

  // Upload file mutation
  const uploadFileMutation = useMutation({
    mutationFn: async (file: File) => {
      const fileExt = file.name.split('.').pop();
      const fileName = `${proposalId}/${Date.now()}.${fileExt}`;
      
      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("proposal-documents")
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Save metadata to database
      const { data: docData, error: docError } = await supabase
        .from("proposal_documents")
        .insert({
          proposal_id: proposalId,
          file_name: file.name,
          file_path: uploadData.path,
          file_size: file.size,
          mime_type: file.type,
          uploaded_by: (await supabase.auth.getUser()).data.user?.id
        })
        .select()
        .single();

      if (docError) throw docError;
      return docData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["proposal-documents", proposalId] });
      toast({
        title: "Success",
        description: "File uploaded successfully",
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to upload file",
        variant: "destructive",
      });
    },
  });

  // Add Google Drive link mutation
  const addDriveLinkMutation = useMutation({
    mutationFn: async (url: string) => {
      const { data, error } = await supabase
        .from("proposal_documents")
        .insert({
          proposal_id: proposalId,
          file_name: "Google Drive Document",
          file_path: "",
          drive_url: url,
          uploaded_by: (await supabase.auth.getUser()).data.user?.id
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["proposal-documents", proposalId] });
      setDriveUrl("");
      setIsAddingDriveLink(false);
      toast({
        title: "Success",
        description: "Google Drive link added successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add Google Drive link",
        variant: "destructive",
      });
    },
  });

  // Delete document mutation
  const deleteDocumentMutation = useMutation({
    mutationFn: async (document: any) => {
      // Delete from storage if it's a file upload
      if (document.file_path) {
        const { error: storageError } = await supabase.storage
          .from("proposal-documents")
          .remove([document.file_path]);
        
        if (storageError) throw storageError;
      }

      // Delete from database
      const { error } = await supabase
        .from("proposal_documents")
        .delete()
        .eq("id", document.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["proposal-documents", proposalId] });
      toast({
        title: "Success",
        description: "Document deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete document",
        variant: "destructive",
      });
    },
  });

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "Error",
        description: "File size must be less than 10MB",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);
    try {
      await uploadFileMutation.mutateAsync(file);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (document: any) => {
    if (document.drive_url) {
      window.open(document.drive_url, "_blank");
      return;
    }

    try {
      const { data, error } = await supabase.storage
        .from("proposal-documents")
        .download(document.file_path);

      if (error) throw error;

      // Create download link
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = document.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to download file",
        variant: "destructive",
      });
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Documents & Attachments</CardTitle>
          {!isReadonly && (
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                <Upload className="h-4 w-4 mr-2" />
                {uploading ? "Uploading..." : "Upload File"}
              </Button>
              
              <Dialog open={isAddingDriveLink} onOpenChange={setIsAddingDriveLink}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Add Drive Link
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Google Drive Link</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="drive-url">Google Drive URL</Label>
                      <Input
                        id="drive-url"
                        placeholder="https://drive.google.com/..."
                        value={driveUrl}
                        onChange={(e) => setDriveUrl(e.target.value)}
                      />
                    </div>
                    <div className="flex justify-end space-x-2">
                      <Button
                        variant="outline"
                        onClick={() => setIsAddingDriveLink(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={() => addDriveLinkMutation.mutate(driveUrl)}
                        disabled={!driveUrl || addDriveLinkMutation.isPending}
                      >
                        {addDriveLinkMutation.isPending ? "Adding..." : "Add Link"}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                style={{ display: "none" }}
                onChange={handleFileUpload}
              />
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-4">Loading documents...</div>
        ) : documents && documents.length > 0 ? (
          <div className="space-y-3">
            {documents.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center justify-between p-3 border rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  {doc.drive_url ? (
                    <ExternalLink className="h-5 w-5 text-blue-600" />
                  ) : (
                    <FileText className="h-5 w-5 text-muted-foreground" />
                  )}
                  <div>
                    <p className="font-medium">{doc.file_name}</p>
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      {doc.file_size && (
                        <span>{formatFileSize(doc.file_size)}</span>
                      )}
                      <span>•</span>
                      <span>{format(new Date(doc.created_at), "MMM dd, yyyy")}</span>
                      {doc.drive_url && (
                        <>
                          <span>•</span>
                          <Badge variant="secondary">Google Drive</Badge>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDownload(doc)}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  {!isReadonly && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteDocumentMutation.mutate(doc)}
                      disabled={deleteDocumentMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No documents attached yet</p>
            {!isReadonly && (
              <p className="text-sm">Upload files or add Google Drive links to get started</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}